import * as React from 'react';
import type { IMegaMenuConfig, IMegaMenuContext, IMegaMenuPersistenceManager } from './megamenu/definitions';
import { AppVisualStateContext } from './appshellcontext';
import { MegaMenuData } from './megamenu/megamenudata';
import type { ILocationMatchProp } from '@@data/entities';
import { getCookie } from '@inwink/utils/cookies';
import { setCookie } from '@@services/cookieservice';

export interface IAppShellMenuConfig {
    key: string;
    config: IMegaMenuConfig;
    match: ILocationMatchProp;
    persistence?: IMegaMenuPersistenceManager;
}

export interface IAppShellMenuUIState {
    allowFixed?: boolean;
    collapsed?: boolean;
    collapsedWhenLarge?: boolean;
    allowSubmenuTooltip?: boolean;
}

export interface IAppShellMenuState extends IAppShellMenuUIState {
    menuConfig?: IAppShellMenuConfig;
    toggleMenu: (persist?: boolean) => void;
}

export interface IAppShellMenuManagerContext {
    registerMenu(config: IAppShellMenuConfig);
    unregisterMenu(config: IAppShellMenuConfig);
}

export const AppShellMenuManagerContext = React.createContext<IAppShellMenuManagerContext>(null);
export const AppShellMenuContext = React.createContext<IAppShellMenuState>(null);


const limitForFixedMenu = "L";
const limitForTooltipMenu = "M";

export interface IAppShellMenuManagerProps extends IMegaMenuContext {
    children?: React.ReactNode;
    visualstate: string[];
}

export interface IAppShellMenuManagerState {
    uistate: IAppShellMenuUIState;
    allMenuConfigs: IAppShellMenuConfig[];
    currentConfig?: IAppShellMenuConfig;
    currentContext?: IAppShellMenuState;
    menuManager: IAppShellMenuManagerContext;
    forceCollaped: boolean;
}

const cookieName = "appmenucollapsed";

export class AppShellMenuManager extends React.Component<IAppShellMenuManagerProps, IAppShellMenuManagerState> {
    constructor(props: IAppShellMenuManagerProps) {
        super(props);
        const collapsed = getCookie(cookieName);
        const forceCollasped = collapsed === "true";
        const uistate = this.visualStateChanged({}, props.visualstate, forceCollasped);
        this.state = {
            forceCollaped: forceCollasped,
            menuManager: {
                registerMenu: (config) => {
                    this.setState((currentState) => {
                        const newMenuConfigs = [...currentState.allMenuConfigs];
                        newMenuConfigs.push(config);

                        return {
                            allMenuConfigs: newMenuConfigs,
                            currentConfig: config,
                            currentContext: {
                                ...currentState.uistate,
                                menuConfig: config,
                                toggleMenu: this.toggleMenu
                            }
                        };
                    });
                },
                unregisterMenu: (config) => {
                    this.setState((currentState) => {
                        const newMenuConfigs = [...currentState.allMenuConfigs];
                        let idx;
                        while ((idx = newMenuConfigs.indexOf(config)) >= 0) {
                            newMenuConfigs.splice(idx, 1);
                        }

                        const newConfig = newMenuConfigs[newMenuConfigs.length - 1];
                        return {
                            allMenuConfigs: newMenuConfigs,
                            currentConfig: newConfig,
                            currentContext: {
                                ...currentState.uistate,
                                menuConfig: newConfig,
                                toggleMenu: this.toggleMenu
                            }
                        };
                    });
                }
            },
            allMenuConfigs: [],
            uistate: uistate,
            currentContext: {
                ...uistate,
                toggleMenu: this.toggleMenu
            }
        };
    }

    componentDidUpdate(prevProps: Readonly<IAppShellMenuManagerProps>): void {
        if (prevProps.visualstate !== this.props.visualstate) {

            this.setState((currentState) => {
                const newuistate = this.visualStateChanged(currentState.uistate,
                    this.props.visualstate, this.state.forceCollaped);
                return {
                    uistate: newuistate,
                    currentContext: {
                        ...newuistate,
                        menuConfig: currentState.currentConfig,
                        toggleMenu: this.toggleMenu
                    }
                };
            });
        }
    }

    toggleMenu = (persist?: boolean) => {
        const state = {} as Partial<IAppShellMenuUIState>;
        state.collapsed = !this.state.uistate.collapsed;

        if (persist) {
            setCookie(cookieName, state.collapsed.toString());
        }

        const isLarge = (this.props.visualstate.indexOf(limitForFixedMenu) >= 0);
        if (isLarge) {
            state.collapsedWhenLarge = state.collapsed;
        }

        this.setState((prevState) => {
            const newuistate = { ...prevState.uistate, ...state };
            return {
                uistate: newuistate,
                currentContext: {
                    ...newuistate,
                    menuConfig: prevState.currentConfig,
                    toggleMenu: this.toggleMenu
                }
            } as any;
        });
    };

    visualStateChanged(uistate: IAppShellMenuUIState, visualstate: string[], forceCollapsed: boolean): IAppShellMenuUIState {
        const isLarge = (visualstate.indexOf(limitForFixedMenu) >= 0);
        const allowmenutooltip = (visualstate.indexOf(limitForTooltipMenu) >= 0);
        const menupatch: any = {};
        if (allowmenutooltip) {
            menupatch.allowSubmenuTooltip = true;
        } else {
            menupatch.allowSubmenuTooltip = false;
        }

        if (isLarge) {
            menupatch.allowFixed = true;
            menupatch.collapsed = uistate.collapsedWhenLarge;
        } else if (allowmenutooltip) {
            menupatch.allowFixed = true;
            menupatch.collapsed = true;
        } else {
            menupatch.allowFixed = false;
            menupatch.collapsed = true;
        }

        if (!menupatch.allowFixed) {
            menupatch.allowSubmenuTooltip = true;
        }
        if (forceCollapsed) {
            menupatch.collapsed = true;
        }
        return { ...uistate, ...menupatch };
    }

    render() {
        return <AppShellMenuManagerContext.Provider value={this.state.menuManager}>
            <AppShellMenuContext.Provider value={this.state.currentContext}>
                <MegaMenuData
                    {...this.props}
                    menuKey={this.state.currentConfig?.key}
                    match={this.state.currentConfig?.match}
                    config={this.state.currentConfig?.config}
                    isMobile={this.props.isMobile}
                    persistence={this.state.currentConfig?.persistence}
                >
                    {this.props.children}
                </MegaMenuData>
            </AppShellMenuContext.Provider>
        </AppShellMenuManagerContext.Provider>;
    }
}

export function AppShellMenuManagerWithVisualState(props: IMegaMenuContext & { children?: React.ReactNode; }) {
    const visualState = React.useContext(AppVisualStateContext);

    return <AppShellMenuManager {...props} visualstate={visualState} />;
}

export function AppShellMenuRegistration(props: { children?: JSX.Element; menuConfig: IAppShellMenuConfig }): JSX.Element {
    const menuManager = React.useContext(AppShellMenuManagerContext);
    React.useEffect(() => {
        menuManager.registerMenu(props.menuConfig);
        return () => {
            menuManager.unregisterMenu(props.menuConfig);
        };
    }, [props.menuConfig]);

    return props.children;
}